// 134 #145 -- derive a richer event status for the status badge/legend.
//
// The persisted EventPublic.status is only draft | published | cancelled. The BA
// wants the published state split by how "complete" the event is and where it
// sits relative to now: info-only (no matches yet), with-matches, live (in
// progress) and completed (already finished). This module is the single pure
// source of truth for that derivation so the calendar list, week grid and event
// detail header all render the same state. Live/Completed are derived from the
// client's view of `now` vs starts_at/ends_at -- no server push (see iteration
// scope: refresh on reload is sufficient for v1).

export type EventStatusState =
  | "draft"
  | "published_info"
  | "published_matches"
  | "live"
  | "completed"
  | "cancelled";

/** Minimal shape this derivation needs -- a structural subset of EventPublic. */
export interface EventStatusInput {
  status: "draft" | "published" | "cancelled";
  starts_at: string; // ISO 8601
  ends_at: string; // ISO 8601
  match_count: number;
}

/**
 * Derive the badge state from the event's persisted fields + the current time.
 *
 * Precedence:
 *   1. cancelled  -- a cancelled event is cancelled regardless of timing/matches.
 *   2. draft      -- unpublished work-in-progress.
 *   3. published, evaluated against `now`:
 *        - completed         when ends_at < now
 *        - live              when starts_at <= now <= ends_at
 *        - otherwise (future): published_matches if it has matches,
 *                              published_info if it has none.
 *
 * `now` is injectable for deterministic tests; defaults to the call-time clock.
 */
export function deriveEventStatus(event: EventStatusInput, now: Date = new Date()): EventStatusState {
  if (event.status === "cancelled") return "cancelled";
  if (event.status === "draft") return "draft";

  // status === "published"
  const nowMs = now.getTime();
  const startMs = new Date(event.starts_at).getTime();
  const endMs = new Date(event.ends_at).getTime();

  // Past (already finished) wins over the in-window check so an event whose
  // ends_at has just passed reads as completed, not live.
  if (Number.isFinite(endMs) && endMs < nowMs) return "completed";
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && startMs <= nowMs && nowMs <= endMs) {
    return "live";
  }

  // Future / upcoming published event -- split by whether matches exist.
  return event.match_count > 0 ? "published_matches" : "published_info";
}

/** Short human label per state, for the badge text. */
export const EVENT_STATUS_LABELS: Record<EventStatusState, string> = {
  draft: "Draft",
  published_info: "Published",
  published_matches: "Scheduled",
  live: "Live",
  completed: "Completed",
  cancelled: "Cancelled",
};

/** Longer description used as the badge tooltip / legend copy. */
export const EVENT_STATUS_DESCRIPTIONS: Record<EventStatusState, string> = {
  draft: "Draft -- saved but not yet published to parents and coaches",
  published_info: "Published -- information only, no matches scheduled yet",
  published_matches: "Published with matches scheduled",
  live: "Live -- this event is currently in progress",
  completed: "Completed -- this event has finished",
  cancelled: "Cancelled",
};

/**
 * Inverted-contrast Tailwind classes per state (light bg + darker same-hue text),
 * matching the project badge pattern and the existing draft/published/cancelled
 * styling. Includes dark-mode variants.
 */
export const EVENT_STATUS_CLASSES: Record<EventStatusState, string> = {
  draft: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  published_info: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  published_matches: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  live: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  completed: "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200",
};
